import React, { useState, useEffect, useRef } from 'react';
import { Book, Maximize2, DownloadCloud, Share2, Check, Trash2, ChevronDown, FileImage, FileText, Pencil } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fmtWeight } from '../utils/format';

// HTML-escape for å forhindre XSS i innerHTML-templates
function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Lagre/del et fangstkort.
// Mobil: bruk Web Share med fil -> brukeren får "Lagre bilde" rett i kamerarullen.
// Desktop (eller hvis deling ikke støttes): vanlig nedlasting til Nedlastinger.
//
// VIKTIG: eksporteres som JPEG, ikke PNG. Apper som Snapchat (og enkelte
// bilde-gallerier) laster ofte inn store/høye PNG-er generert fra canvas feil
// eller ikke i det hele tatt — samme problem som er løst for kamerarull-bilder
// i CaptureScreen.js. Containeren har alltid hvit bakgrunn, så JPEG uten alfa
// er trygt her.
async function saveCanvas(canvas, filename) {
  // Konverter canvas til en JPEG-blob
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.92)
  );
  if (!blob) throw new Error('Kunne ikke lage bildefil');

  // Prøv native deling med fil (iOS/Android) — gir "Lagre bilde" rett i kamerarullen
  const file = new File([blob], filename, { type: 'image/jpeg' });
  const canShareFile =
    typeof navigator !== 'undefined' &&
    navigator.canShare &&
    navigator.canShare({ files: [file] });

  if (canShareFile) {
    try {
      await navigator.share({ files: [file], title: 'FiskDa fangstkort', text: 'Sjekk ut denne fangsten jeg fikk!' });
      return; // delt/lagret
    } catch (err) {
      // Bruker avbrøt delingsarket — ikke fall tilbake til nedlasting
      if (err && err.name === 'AbortError') return;
      // Andre feil: fall gjennom til vanlig nedlasting
    }
  }

  // Fallback: vanlig nedlasting via blob-URL
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = filename;
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Frigjør URL litt etter at nedlastingen har startet
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Custom orange marker icon
const orangeIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
      <path fill="#E67E22" stroke="#fff" stroke-width="2" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.4 12.5 28.5 12.5 28.5S25 20.9 25 12.5C25 5.6 19.4 0 12.5 0z"/>
      <circle cx="12.5" cy="12.5" r="5" fill="#fff"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

export default function DetailView({ item, onShare, onDelete, onEdit, onZoom, copyFeedback }) {
  const [style, setStyle] = useState('classic');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef(null);

  // Lukk meny ved klikk utenfor
  useEffect(() => {
    function handleClickOutside(event) {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)) {
        setShowDownloadMenu(false);
      }
    }

    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDownloadMenu]);

  // Generer kart-URL basert på koordinater
  const generateMapUrl = (lat, lng, privacy) => {
    const zoom = privacy === 'Nøyaktig' ? 15 : privacy === '100m' ? 14 : 12;
    
    // Beregn tile-koordinater for OpenStreetMap
    const tileX = Math.floor((lng + 180) / 360 * Math.pow(2, zoom));
    const tileY = Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom));
    
    // Bruk OpenStreetMap tiles direkte (helt gratis).
    // Subdomene (a/b/c) kreves for å passere CSP (https://*.tile.openstreetmap.org).
    return `https://a.tile.openstreetmap.org/${zoom}/${tileX}/${tileY}.png`;
  };

  // Capture kart som bilde (forenklet versjon)
  const captureMapAsImage = async () => {
    try {
      if (!item.location || !item.privacy || item.privacy === 'Ingen') {
        return null;
      }

      // Opprett canvas for å bygge kartet.
      // Større oppløsning + sideforhold tilpasset rammen (~2.87:1) så
      // object-fit: cover ikke croper bort noe og kartet fyller rammen.
      const canvas = document.createElement('canvas');
      const MAP_W = 1200;
      const MAP_H = 420;
      canvas.width = MAP_W;
      canvas.height = MAP_H;
      const ctx = canvas.getContext('2d');
      
      // Fyll bakgrunn
      ctx.fillStyle = '#f8f5f0';
      ctx.fillRect(0, 0, MAP_W, MAP_H);
      
      const zoom = item.privacy === 'Nøyaktig' ? 15 : item.privacy === '100m' ? 14 : 12;
      
      // Mer presis beregning av tile-koordinater
      const lat = item.location.lat;
      const lng = item.location.lng;
      
      // Konverter til tile-koordinater med høyere presisjon
      const tileSize = 256;
      const n = Math.pow(2, zoom);
      
      // Beregn eksakte pixel-koordinater
      const pixelX = ((lng + 180) / 360) * n * tileSize;
      const latRad = lat * Math.PI / 180;
      const pixelY = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n * tileSize;
      
      // Beregn hvilke tiles vi trenger for å dekke kart-området
      const centerTileX = Math.floor(pixelX / tileSize);
      const centerTileY = Math.floor(pixelY / tileSize);
      
      // Beregn nøyaktig offset innenfor tile
      const offsetX = pixelX - (centerTileX * tileSize);
      const offsetY = pixelY - (centerTileY * tileSize);

      // Senterpunkt på canvas
      const mapCenterX = MAP_W / 2;
      const mapCenterY = MAP_H / 2;

      // Hvor mange tiles trengs i hver retning for å dekke hele canvaset
      // (+1 margin så kantene aldri blir tomme)
      const tilesX = Math.ceil(MAP_W / tileSize / 2) + 1;
      const tilesY = Math.ceil(MAP_H / tileSize / 2) + 1;
      
      // Last inn tiles i et rutenett som garantert dekker hele flaten
      const tilePromises = [];
      for (let x = -tilesX; x <= tilesX; x++) {
        for (let y = -tilesY; y <= tilesY; y++) {
          const tileX = centerTileX + x;
          const tileY = centerTileY + y;
          // Subdomene a/b/c fordeler last og kreves av CSP (*.tile.openstreetmap.org)
          const sub = ['a', 'b', 'c'][Math.abs(tileX + tileY) % 3];
          const tileUrl = `https://${sub}.tile.openstreetmap.org/${zoom}/${tileX}/${tileY}.png`;
          
          const promise = new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve({ img, x, y });
            img.onerror = () => resolve({ img: null, x, y });
            img.src = tileUrl;
          });
          
          tilePromises.push(promise);
        }
      }
      
      // Vent på at alle tiles laster
      const tiles = await Promise.all(tilePromises);
      
      // Tegn tiles på canvas med perfekt sentrering
      tiles.forEach(({ img, x, y }) => {
        if (img) {
          // Beregn nøyaktig posisjon for perfekt sentrering
          const drawX = mapCenterX - offsetX + (x * tileSize);
          const drawY = mapCenterY - offsetY + (y * tileSize);
          ctx.drawImage(img, drawX, drawY, tileSize, tileSize);
        }
      });
      
      // Legg til marker/sirkel overlay - eksakt som i originalen
      const centerX = mapCenterX;
      const centerY = mapCenterY;
      
      if (item.privacy === 'Nøyaktig') {
        // Tegn orange marker (samme som i originalen)
        ctx.fillStyle = '#E67E22';
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 3;
        
        // Marker form (dråpe)
        ctx.beginPath();
        ctx.arc(centerX, centerY - 14, 16, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
        
        // Hvit prikk i midten
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(centerX, centerY - 14, 6, 0, 2 * Math.PI);
        ctx.fill();
      } else {
        // Tegn personvern-sirkel (maskert posisjon)
        const radius = item.privacy === '100m' ? 60 : 95;
        
        ctx.strokeStyle = '#E67E22';
        ctx.fillStyle = 'rgba(230, 126, 34, 0.2)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      }
      
      // Legg til attribution (mindre og mer diskret)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fillRect(5, MAP_H - 16, 120, 14);
      ctx.fillStyle = 'rgba(59, 77, 60, 0.6)';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('© OpenStreetMap', 8, MAP_H - 6);
      
      return canvas.toDataURL('image/png');
      
    } catch (error) {
      console.error('Feil ved kart-generering:', error);
      return null;
    }
  };

  // Konverter URL til base64 for bruk i eksport-templates
  const imageToBase64 = async (src) => {
    // Allerede base64
    if (src.startsWith('data:')) return src;
    
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn('Kunne ikke konvertere bilde til base64:', err);
      return src; // Fallback til original URL
    }
  };

  // Nedlastingsfunksjoner
  const downloadAsImage = async (downloadStyle) => {
    try {
      console.log('Starting download for style:', downloadStyle);
      
      // Capture kart først hvis det finnes
      let mapImageData = null;
      if (item.location && item.privacy && item.privacy !== 'Ingen') {
        console.log('Generating map...');
        mapImageData = await captureMapAsImage();
        console.log('Map generated:', mapImageData ? 'success' : 'failed');
      }

      // Konverter bilde til base64 for pålitelig eksport (Storage URLs kan gi CORS-problemer)
      const imageBase64 = await imageToBase64(item.image);

      // Opprett en midlertidig container
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.background = 'white';
      container.style.fontFamily = 'serif';
      
      // Sett størrelse basert på stil
      if (downloadStyle === 'clean') {
        container.style.width = '800px';
        container.style.height = 'auto'; // La høyden tilpasse seg bildet
      } else {
        container.style.width = '900px';
        container.style.height = 'auto'; // La høyden tilpasse seg innholdet
      }
      
      document.body.appendChild(container);
      
      console.log('Generating HTML template...');
      // Generer HTML for nedlasting med inline templates
      try {
        // Escape alle brukerdata for sikker innerHTML-bruk
          const esc = {
            species: escapeHtml(item.species),
            latin: escapeHtml(item.latin),
            weight: escapeHtml(item.weight),
            privacy: escapeHtml(item.privacy),
            weatherTemp: escapeHtml(item.weather?.temp),
            weatherWind: escapeHtml(item.weather?.wind),
            weatherWindDir: escapeHtml(item.weather?.windDir),
            weatherCondition: escapeHtml(item.weather?.condition),
            advLength: escapeHtml(item.advanced?.length),
            advCircumference: escapeHtml(item.advanced?.circumference),
            advWidth: escapeHtml(item.advanced?.width),
            advGear: escapeHtml(item.advanced?.gear),
            advLure: escapeHtml(item.advanced?.lure),
            advNotes: escapeHtml(item.advanced?.notes),
          };

        if (downloadStyle === 'clean') {
          // Clean inline template - rammen tilpasser seg bildet
          const date = new Date(item.caughtAt).toLocaleDateString('no-NO');
          const time = item.time && item.time.includes(':') ? item.time : item.time ? `${item.time.slice(0,2)}:${item.time.slice(2)}` : '';
          
          container.innerHTML = `
            <div style="position: relative; width: 800px; background: white;">
              <img src="${imageBase64}" style="width: 100%; height: auto; display: block;" />
              <div style="position: absolute; inset: 0; background: linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 50%, transparent 100%); padding: 48px; display: flex; flex-direction: column; justify-content: flex-end;">
                <h2 style="font-size: 4rem; font-weight: 900; font-style: italic; text-transform: uppercase; line-height: 0.9; color: white; margin-bottom: 24px; letter-spacing: -0.05em;">
                  ${esc.species}
                </h2>
                ${item.latin ? `<p style="font-style: italic; font-size: 1.25rem; color: rgba(255,255,255,0.7); margin-bottom: 24px;">${esc.latin}</p>` : ''}
                
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; border-top: 1px solid rgba(255,255,255,0.2); padding-top: 24px; margin-bottom: 16px;">
                  <div>
                    <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(255,255,255,0.4); letter-spacing: 0.3em; margin-bottom: 4px;">Feltdato</p>
                    <p style="font-style: italic; font-size: 1.25rem; color: white;">${date}</p>
                    ${time ? `<p style="font-style: italic; font-size: 0.875rem; color: rgba(255,255,255,0.6); margin-top: 4px;">${time}</p>` : ''}
                  </div>
                  ${item.weight ? `
                    <div>
                      <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(255,255,255,0.4); letter-spacing: 0.3em; margin-bottom: 4px;">Vekt</p>
                      <p style="font-size: 2.5rem; font-weight: 900; font-style: italic; color: #E67E22;">${esc.weight}g</p>
                    </div>
                  ` : ''}
                </div>

                <div style="margin-bottom: 16px;">
                  <div>
                    <p style="font-size: 0.5rem; font-weight: 900; text-transform: uppercase; color: rgba(255,255,255,0.4); letter-spacing: 0.3em; margin-bottom: 4px;">Habitat</p>
                    <p style="font-size: 0.875rem; font-style: italic; color: white;">${item.waterType === 'salt' ? 'Saltvann' : 'Ferskvann'}</p>
                  </div>
                </div>

                <div style="border-top: 1px solid rgba(255,255,255,0.1); padding-top: 16px; margin-top: 16px; display: flex; justify-content: space-between; font-size: 0.5rem; color: rgba(255,255,255,0.4); font-style: italic;">
                  <span>FiskDa Felthåndbok</span>
                  <span>NO-${item.caughtAt.toString().slice(-6)}</span>
                </div>
              </div>
            </div>
          `;
        } else {
          // Classic PNG template - lik PDF men uten "Fangstrapport" header og rapport ID
          const date = new Date(item.caughtAt).toLocaleDateString('no-NO');
          const time = item.time && item.time.includes(':') ? item.time : item.time ? `${item.time.slice(0,2)}:${item.time.slice(2)}` : '';
          
          container.innerHTML = `
            <div style="background: white; padding: 48px; font-family: serif; width: 900px; box-sizing: border-box;">
              <!-- Header -->
              <div style="border-bottom: 2px solid rgba(59,77,60,0.12); padding-bottom: 22px; margin-bottom: 30px;">
                <h2 style="font-size: 3.25rem; font-weight: 900; font-style: italic; text-transform: uppercase; line-height: 0.95; color: #2D3436; letter-spacing: -0.03em; margin: 0;">
                  ${esc.species}
                </h2>
                ${item.latin ? `<p style="font-style: italic; font-size: 1.2rem; color: rgba(59,77,60,0.5); margin: 8px 0 0 0;">${esc.latin}</p>` : ''}
              </div>

              <!-- Hovedbilde: begrenset høyde, hele bildet vises (ingen crop) -->
              <div style="text-align: center; margin-bottom: 30px;">
                <img src="${imageBase64}" style="max-width: 100%; max-height: 520px; width: auto; height: auto; display: inline-block; border-radius: 4px; box-shadow: 0 8px 20px rgba(0,0,0,0.12); border: 1px solid rgba(209,204,192,0.3); filter: grayscale(0.15);" />
              </div>

              <!-- Grunnleggende data -->
              <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; border-top: 1px solid rgba(59,77,60,0.1); padding-top: 26px; margin-bottom: 26px;">
                <div>
                  <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.55); letter-spacing: 0.2em; font-style: italic; margin: 0 0 7px 0;">Dato</p>
                  <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${date}</p>
                </div>
                ${time ? `
                  <div>
                    <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.55); letter-spacing: 0.2em; font-style: italic; margin: 0 0 7px 0;">Klokkeslett</p>
                    <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${time}</p>
                  </div>
                ` : ''}
                ${item.weight ? `
                  <div>
                    <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.55); letter-spacing: 0.2em; font-style: italic; margin: 0 0 7px 0;">Målt Vekt</p>
                    <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${esc.weight} gram</p>
                  </div>
                ` : ''}
                <div>
                  <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.55); letter-spacing: 0.2em; font-style: italic; margin: 0 0 7px 0;">Habitat</p>
                  <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${item.waterType === 'salt' ? 'Saltvann' : 'Ferskvann'}</p>
                </div>
              </div>

              <!-- Værforhold -->
              ${(item.weather?.temp || item.weather?.wind || item.weather?.condition || item.weather?.windDir) ? `
                <div style="background: #F8F5F0; padding: 26px; border-radius: 4px; border: 1px solid rgba(209,204,192,0.3); margin-bottom: 26px;">
                  <h3 style="font-size: 0.8rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.6); letter-spacing: 0.2em; font-style: italic; border-bottom: 1px solid rgba(209,204,192,0.4); padding-bottom: 11px; margin: 0 0 20px 0;">
                    Værforhold
                  </h3>
                  <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px;">
                    ${item.weather?.temp ? `
                      <div>
                        <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 7px 0;">Temperatur</p>
                        <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${esc.weatherTemp}°C</p>
                      </div>
                    ` : ''}
                    ${item.weather?.wind ? `
                      <div>
                        <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 7px 0;">Vind</p>
                        <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${esc.weatherWind} m/s</p>
                      </div>
                    ` : ''}
                    ${item.weather?.windDir ? `
                      <div>
                        <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 7px 0;">Vindretning</p>
                        <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${esc.weatherWindDir}</p>
                      </div>
                    ` : ''}
                    ${item.weather?.condition ? `
                      <div>
                        <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 7px 0;">Forhold</p>
                        <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${esc.weatherCondition}</p>
                      </div>
                    ` : ''}
                  </div>
                </div>
              ` : ''}

              <!-- Mål & Utstyr -->
              ${(item.advanced?.length || item.advanced?.circumference || item.advanced?.width || item.advanced?.gear || item.advanced?.lure || item.advanced?.notes) ? `
                <div style="background: #F8F5F0; padding: 26px; border-radius: 4px; border: 1px solid rgba(209,204,192,0.3); margin-bottom: 26px;">
                  <h3 style="font-size: 0.8rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.6); letter-spacing: 0.2em; font-style: italic; border-bottom: 1px solid rgba(209,204,192,0.4); padding-bottom: 11px; margin: 0 0 20px 0;">
                    Mål & Utstyr
                  </h3>
                  
                  ${(item.advanced?.length || item.advanced?.circumference || item.advanced?.width) ? `
                    <div style="margin-bottom: 20px;">
                      <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 11px 0;">Fysiske mål</p>
                      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px;">
                        ${item.advanced?.length ? `
                          <div>
                            <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 7px 0;">Lengde</p>
                            <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${esc.advLength} cm</p>
                          </div>
                        ` : ''}
                        ${item.advanced?.circumference ? `
                          <div>
                            <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 7px 0;">Omkrets</p>
                            <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${esc.advCircumference} cm</p>
                          </div>
                        ` : ''}
                        ${item.advanced?.width ? `
                          <div>
                            <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 7px 0;">Bredde</p>
                            <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${esc.advWidth} cm</p>
                          </div>
                        ` : ''}
                      </div>
                    </div>
                  ` : ''}
                  
                  ${(item.advanced?.gear || item.advanced?.lure) ? `
                    <div style="padding-top: 16px; border-top: 1px solid rgba(209,204,192,0.4); margin-bottom: 16px;">
                      <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 11px 0;">Utstyr</p>
                      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px;">
                        ${item.advanced?.gear ? `
                          <div>
                            <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 7px 0;">Fiskeutstyr</p>
                            <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${esc.advGear}</p>
                          </div>
                        ` : ''}
                        ${item.advanced?.lure ? `
                          <div>
                            <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 7px 0;">Agn/Sluk</p>
                            <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${esc.advLure}</p>
                          </div>
                        ` : ''}
                      </div>
                    </div>
                  ` : ''}
                  
                  ${item.advanced?.notes ? `
                    <div style="padding-top: 16px; border-top: 1px solid rgba(209,204,192,0.4);">
                      <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 7px 0;">Feltnotater</p>
                      <p style="font-style: italic; font-size: 1.2rem; line-height: 1.55; color: #3B4D3C; white-space: pre-line; border-left: 3px solid rgba(230,126,34,0.35); padding-left: 16px; margin: 0;">
                        ${esc.advNotes}
                      </p>
                    </div>
                  ` : ''}
                </div>
              ` : ''}

              <!-- Posisjon -->
              ${(item.location && item.privacy && item.privacy !== 'Ingen') ? `
                <div style="background: #F8F5F0; padding: 26px; border-radius: 4px; border: 1px solid rgba(209,204,192,0.3);">
                  <h3 style="font-size: 0.8rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.6); letter-spacing: 0.2em; font-style: italic; border-bottom: 1px solid rgba(209,204,192,0.4); padding-bottom: 11px; margin: 0 0 20px 0;">
                    Posisjon
                  </h3>
                  
                  <!-- Kart: fyller hele rammen -->
                  <div style="height: 300px; border-radius: 6px; overflow: hidden; border: 1px solid #D1CCC0; box-shadow: inset 0 2px 4px rgba(0,0,0,0.1); margin-bottom: 20px; background: #f8f5f0;">
                    ${mapImageData ? `
                      <img 
                        src="${mapImageData}" 
                        style="display: block; width: 100%; height: 100%; object-fit: cover;"
                        alt="Kart over fangststed"
                      />
                    ` : `
                      <div style="width: 100%; height: 100%; background: #f8f5f0; display: flex; align-items: center; justify-content: center; color: rgba(59,77,60,0.4); font-style: italic;">
                        Kart ikke tilgjengelig
                      </div>
                    `}
                  </div>
                  
                  <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px;">
                    <div>
                      <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 7px 0;">Nøyaktighet</p>
                      <p style="font-size: 1.35rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1; margin: 0;">${esc.privacy}</p>
                    </div>
                    <div>
                      <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.45); font-style: italic; letter-spacing: 0.2em; margin: 0 0 7px 0;">Koordinater</p>
                      <p style="font-size: 1rem; font-family: monospace; color: rgba(59,77,60,0.6); margin: 0;">
                        ${item.privacy === 'Nøyaktig' 
                          ? `${item.location.lat.toFixed(6)}, ${item.location.lng.toFixed(6)}`
                          : `±${esc.privacy}`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              ` : ''}
            </div>
          `;
        }
        console.log('HTML template generated successfully');
      } catch (templateError) {
        console.error('Template generation failed:', templateError);
        throw new Error('Template generation failed: ' + templateError.message);
      }
      
      // Vent på at bilder laster
      console.log('Waiting for images to load...');
      const images = container.querySelectorAll('img');
      console.log('Found', images.length, 'images');
      
      await Promise.all(Array.from(images).map((img, index) => {
        return new Promise((resolve) => {
          if (img.complete) {
            console.log(`Image ${index} already loaded`);
            resolve();
          } else {
            img.onload = () => {
              console.log(`Image ${index} loaded successfully`);
              resolve();
            };
            img.onerror = (error) => {
              console.log(`Image ${index} failed to load:`, error);
              resolve(); // Continue even if image fails
            };
          }
        });
      }));
      
      console.log('All images processed, starting html2canvas...');
      
      // Bruk html2canvas
      const html2canvas = (await import('html2canvas')).default;
      const canvasElement = await html2canvas(container, {
        backgroundColor: 'white',
        scale: 2, // Høyere scale = skarpere bilde (både clean og classic)
        useCORS: true,
        allowTaint: true,
        logging: true,
        width: downloadStyle === 'clean' ? 800 : 900
        // Fjernet height så den tilpasser seg innholdet
      });
      
      console.log('Canvas created, starting download...');
      
      // Last ned / del bildet (kamerarull på mobil, Nedlastinger på desktop)
      const filename = `fiskda-${(item.species || 'fangst').replace(/[^a-zA-Z0-9æøåÆØÅ\-_ ]/g, '')}-${downloadStyle}.jpg`;
      await saveCanvas(canvasElement, filename);
      
      console.log('Download initiated successfully');
      
      // Rydd opp
      document.body.removeChild(container);
      setShowDownloadMenu(false);
      
    } catch (error) {
      console.error('Nedlasting feilet:', error);
      console.error('Error stack:', error.stack);
      alert(`Nedlasting feilet: ${error.message}`);
    }
  };

  const downloadAsPDF = async (downloadStyle) => {
    try {
      // Capture kart først hvis det finnes
      let mapImageData = null;
      if (item.location && item.privacy && item.privacy !== 'Ingen') {
        mapImageData = await captureMapAsImage();
      }

      // Konverter bilde til base64 for pålitelig eksport
      const imageBase64 = await imageToBase64(item.image);

      // Opprett en midlertidig container
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.background = 'white';
      container.style.fontFamily = 'serif';
      container.style.width = '210mm'; // A4 bredde
      container.style.minHeight = '297mm'; // A4 høyde
      container.style.padding = '20mm';
      container.style.boxSizing = 'border-box';
      
      document.body.appendChild(container);
      
      // Generer HTML for PDF med inline templates
      const date = new Date(item.caughtAt).toLocaleDateString('no-NO');
      const time = item.time && item.time.includes(':') ? item.time : item.time ? `${item.time.slice(0,2)}:${item.time.slice(2)}` : '';
      
      // Escape alle brukerdata for sikker innerHTML-bruk
      const esc = {
        species: escapeHtml(item.species),
        latin: escapeHtml(item.latin),
        weight: escapeHtml(item.weight),
        privacy: escapeHtml(item.privacy),
        weatherTemp: escapeHtml(item.weather?.temp),
        weatherWind: escapeHtml(item.weather?.wind),
        weatherWindDir: escapeHtml(item.weather?.windDir),
        weatherCondition: escapeHtml(item.weather?.condition),
        advLength: escapeHtml(item.advanced?.length),
        advCircumference: escapeHtml(item.advanced?.circumference),
        advWidth: escapeHtml(item.advanced?.width),
        advGear: escapeHtml(item.advanced?.gear),
        advLure: escapeHtml(item.advanced?.lure),
        advNotes: escapeHtml(item.advanced?.notes),
      };

      if (downloadStyle === 'clean') {
        container.innerHTML = `
          <div style="position: relative; width: 100%; height: 100%; background: white;">
            <img src="${imageBase64}" style="width: 100%; height: 100%; object-fit: cover; filter: grayscale(0.1);" />
            <div style="position: absolute; inset: 0; background: linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 50%, transparent 100%); padding: 48px; display: flex; flex-direction: column; justify-content: flex-end;">
              <h2 style="font-size: 4rem; font-weight: 900; font-style: italic; text-transform: uppercase; line-height: 0.9; color: white; margin-bottom: 24px; letter-spacing: -0.05em;">
                ${esc.species}
              </h2>
              ${item.latin ? `<p style="font-style: italic; font-size: 1.25rem; color: rgba(255,255,255,0.7); margin-bottom: 24px;">${esc.latin}</p>` : ''}
              
              <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; border-top: 1px solid rgba(255,255,255,0.2); padding-top: 24px; margin-bottom: 16px;">
                <div>
                  <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(255,255,255,0.4); letter-spacing: 0.3em; margin-bottom: 4px;">Feltdato</p>
                  <p style="font-style: italic; font-size: 1.25rem; color: white;">${date}</p>
                  ${time ? `<p style="font-style: italic; font-size: 0.875rem; color: rgba(255,255,255,0.6); margin-top: 4px;">${time}</p>` : ''}
                </div>
                ${item.weight ? `
                  <div>
                    <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(255,255,255,0.4); letter-spacing: 0.3em; margin-bottom: 4px;">Vekt</p>
                    <p style="font-size: 2.5rem; font-weight: 900; font-style: italic; color: #E67E22;">${esc.weight}g</p>
                  </div>
                ` : ''}
              </div>

              <div style="margin-bottom: 16px;">
                <div>
                  <p style="font-size: 0.5rem; font-weight: 900; text-transform: uppercase; color: rgba(255,255,255,0.4); letter-spacing: 0.3em; margin-bottom: 4px;">Habitat</p>
                  <p style="font-size: 0.875rem; font-style: italic; color: white;">${item.waterType === 'salt' ? 'Saltvann' : 'Ferskvann'}</p>
                </div>
              </div>

              <div style="border-top: 1px solid rgba(255,255,255,0.1); padding-top: 16px; margin-top: 16px; display: flex; justify-content: space-between; font-size: 0.5rem; color: rgba(255,255,255,0.4); font-style: italic;">
                <span>FiskDa Felthåndbok</span>
                <span>NO-${item.caughtAt.toString().slice(-6)}</span>
              </div>
            </div>
          </div>
        `;
      } else {
        container.innerHTML = `
          <div style="background: white; padding: 40px; font-family: serif;">
            <!-- Header -->
            <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid rgba(59,77,60,0.1); padding-bottom: 32px; margin-bottom: 40px;">
              <div style="max-width: 75%;">
                <p style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: #E67E22; letter-spacing: 0.5em; font-style: italic; margin-bottom: 8px;">
                  Fangstrapport
                </p>
                <h2 style="font-size: 4rem; font-weight: 900; font-style: italic; text-transform: uppercase; line-height: 0.9; color: #2D3436; letter-spacing: -0.05em; margin-bottom: 8px;">
                  ${esc.species}
                </h2>
                ${item.latin ? `<p style="font-style: italic; font-size: 1.25rem; color: rgba(59,77,60,0.5);">${esc.latin}</p>` : ''}
              </div>
              <div style="text-align: right;">
                <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.5); letter-spacing: 0.3em; font-style: italic; margin-bottom: 4px;">Rapport ID</p>
                <p style="font-size: 1rem; font-weight: 900; font-style: italic; color: #2D3436;">NO-${item.caughtAt.toString().slice(-6)}</p>
              </div>
            </div>

            <!-- Hovedbilde -->
            <img src="${imageBase64}" style="width: 100%; height: auto; display: block; border-radius: 4px; box-shadow: 0 10px 25px rgba(0,0,0,0.1); border: 1px solid rgba(209,204,192,0.2); filter: grayscale(0.2); margin-bottom: 40px;" />

            <!-- Grunnleggende data -->
            <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 32px; border-top: 1px solid rgba(59,77,60,0.1); padding-top: 40px; margin-bottom: 40px;">
              <div>
                <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.5); letter-spacing: 0.3em; font-style: italic; margin-bottom: 8px;">Dato</p>
                <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${date}</p>
              </div>
              ${time ? `
                <div>
                  <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.5); letter-spacing: 0.3em; font-style: italic; margin-bottom: 8px;">Klokkeslett</p>
                  <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${time}</p>
                </div>
              ` : ''}
              ${item.weight ? `
                <div>
                  <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.5); letter-spacing: 0.3em; font-style: italic; margin-bottom: 8px;">Målt Vekt</p>
                  <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${esc.weight} gram</p>
                </div>
              ` : ''}
              <div>
                <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.5); letter-spacing: 0.3em; font-style: italic; margin-bottom: 8px;">Habitat</p>
                <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${item.waterType === 'salt' ? 'Saltvann' : 'Ferskvann'}</p>
              </div>
            </div>

            <!-- Værforhold -->
            ${(item.weather?.temp || item.weather?.wind || item.weather?.condition || item.weather?.windDir) ? `
              <div style="background: #F8F5F0; padding: 32px; border-radius: 4px; border: 1px solid rgba(209,204,192,0.3); margin-bottom: 40px;">
                <h3 style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.6); letter-spacing: 0.3em; font-style: italic; border-bottom: 1px solid rgba(209,204,192,0.4); padding-bottom: 12px; margin-bottom: 24px;">
                  Værforhold
                </h3>
                <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px;">
                  ${item.weather?.temp ? `
                    <div>
                      <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 8px;">Temperatur</p>
                      <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${esc.weatherTemp}°C</p>
                    </div>
                  ` : ''}
                  ${item.weather?.wind ? `
                    <div>
                      <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 8px;">Vind</p>
                      <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${esc.weatherWind} m/s</p>
                    </div>
                  ` : ''}
                  ${item.weather?.windDir ? `
                    <div>
                      <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 8px;">Vindretning</p>
                      <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${esc.weatherWindDir}</p>
                    </div>
                  ` : ''}
                  ${item.weather?.condition ? `
                    <div>
                      <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 8px;">Forhold</p>
                      <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${esc.weatherCondition}</p>
                    </div>
                  ` : ''}
                </div>
              </div>
            ` : ''}

            <!-- Mål & Utstyr -->
            ${(item.advanced?.length || item.advanced?.circumference || item.advanced?.width || item.advanced?.gear || item.advanced?.lure || item.advanced?.notes) ? `
              <div style="background: #F8F5F0; padding: 32px; border-radius: 4px; border: 1px solid rgba(209,204,192,0.3); margin-bottom: 40px;">
                <h3 style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.6); letter-spacing: 0.3em; font-style: italic; border-bottom: 1px solid rgba(209,204,192,0.4); padding-bottom: 12px; margin-bottom: 24px;">
                  Mål & Utstyr
                </h3>
                
                ${(item.advanced?.length || item.advanced?.circumference || item.advanced?.width) ? `
                  <div style="margin-bottom: 24px;">
                    <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 12px;">Fysiske mål</p>
                    <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px;">
                      ${item.advanced?.length ? `
                        <div>
                          <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 8px;">Lengde</p>
                          <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${esc.advLength} cm</p>
                        </div>
                      ` : ''}
                      ${item.advanced?.circumference ? `
                        <div>
                          <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 8px;">Omkrets</p>
                          <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${esc.advCircumference} cm</p>
                        </div>
                      ` : ''}
                      ${item.advanced?.width ? `
                        <div>
                          <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 8px;">Bredde</p>
                          <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${esc.advWidth} cm</p>
                        </div>
                      ` : ''}
                    </div>
                  </div>
                ` : ''}
                
                ${(item.advanced?.gear || item.advanced?.lure) ? `
                  <div style="padding-top: 16px; border-top: 1px solid rgba(209,204,192,0.4); margin-bottom: 16px;">
                    <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 12px;">Utstyr</p>
                    <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px;">
                      ${item.advanced?.gear ? `
                        <div>
                          <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 8px;">Fiskeutstyr</p>
                          <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${esc.advGear}</p>
                        </div>
                      ` : ''}
                      ${item.advanced?.lure ? `
                        <div>
                          <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 8px;">Agn/Sluk</p>
                          <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${esc.advLure}</p>
                        </div>
                      ` : ''}
                    </div>
                  </div>
                ` : ''}
                
                ${item.advanced?.notes ? `
                  <div style="padding-top: 16px; border-top: 1px solid rgba(209,204,192,0.4);">
                    <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 8px;">Feltnotater</p>
                    <p style="font-style: italic; font-size: 1.125rem; line-height: 1.6; color: #3B4D3C; white-space: pre-line; border-left: 2px solid rgba(230,126,34,0.2); padding-left: 16px;">
                      ${esc.advNotes}
                    </p>
                  </div>
                ` : ''}
              </div>
            ` : ''}

            <!-- Posisjon -->
            ${(item.location && item.privacy && item.privacy !== 'Ingen') ? `
              <div style="background: #F8F5F0; padding: 32px; border-radius: 4px; border: 1px solid rgba(209,204,192,0.3); margin-bottom: 40px;">
                <h3 style="font-size: 0.7rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.6); letter-spacing: 0.3em; font-style: italic; border-bottom: 1px solid rgba(209,204,192,0.4); padding-bottom: 12px; margin-bottom: 24px;">
                  Posisjon
                </h3>
                
                <!-- Kart -->
                <div style="height: 256px; border-radius: 6px; overflow: hidden; border: 1px solid #D1CCC0; box-shadow: inset 0 2px 4px rgba(0,0,0,0.1); margin-bottom: 24px; background: #f8f5f0;">
                  ${mapImageData ? `
                    <img 
                      src="${mapImageData}" 
                      style="width: 100%; height: 100%; object-fit: cover;"
                      alt="Kart over fangststed"
                    />
                  ` : `
                    <!-- Fallback hvis kart ikke er tilgjengelig -->
                    <div style="width: 100%; height: 100%; background: #f8f5f0; position: relative;">
                      <!-- Simulert kart-bakgrunn -->
                      <div style="width: 100%; height: 100%; background: linear-gradient(45deg, #e8e5e0 25%, transparent 25%), linear-gradient(-45deg, #e8e5e0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e8e5e0 75%), linear-gradient(-45deg, transparent 75%, #e8e5e0 75%); background-size: 20px 20px; background-position: 0 0, 0 10px, 10px -10px, -10px 0px; opacity: 0.1;"></div>
                      
                      <!-- Marker eller sirkel -->
                      <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);">
                        ${item.privacy === 'Nøyaktig' ? `
                          <!-- Orange marker -->
                          <div style="position: relative;">
                            <div style="width: 25px; height: 25px; background: #E67E22; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>
                            <div style="position: absolute; top: 6px; left: 6px; width: 5px; height: 5px; background: white; border-radius: 50%;"></div>
                          </div>
                        ` : `
                          <!-- Orange sirkel -->
                          <div style="width: ${item.privacy === '100m' ? '80px' : '120px'}; height: ${item.privacy === '100m' ? '80px' : '120px'}; border: 2px solid #E67E22; border-radius: 50%; background: rgba(230,126,34,0.2);"></div>
                        `}
                      </div>
                      
                      <!-- Kart-attributering -->
                      <div style="position: absolute; bottom: 4px; left: 4px; font-size: 0.6rem; color: rgba(59,77,60,0.6); background: rgba(255,255,255,0.8); padding: 2px 6px; border-radius: 3px; font-family: sans-serif;">
                        © OpenStreetMap
                      </div>
                    </div>
                  `}
                </div>
                
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px;">
                  <div>
                    <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 8px;">Nøyaktighet</p>
                    <p style="font-size: 1.25rem; font-weight: 900; font-style: italic; color: #2D3436; line-height: 1;">${esc.privacy}</p>
                  </div>
                  <div>
                    <p style="font-size: 0.6rem; font-weight: 900; text-transform: uppercase; color: rgba(59,77,60,0.4); font-style: italic; letter-spacing: 0.3em; margin-bottom: 8px;">Koordinater</p>
                    <p style="font-size: 1rem; font-family: monospace; color: rgba(59,77,60,0.6);">
                      ${item.privacy === 'Nøyaktig' 
                        ? `${item.location.lat.toFixed(6)}, ${item.location.lng.toFixed(6)}`
                        : `±${esc.privacy}`
                      }
                    </p>
                  </div>
                </div>
              </div>
            ` : ''}

            <!-- Footer -->
            <div style="padding-top: 32px; display: flex; justify-content: space-between; align-items: center; opacity: 0.3; font-style: italic; font-size: 0.6rem; border-top: 1px dashed #D1CCC0;">
              <span>Signert: FiskDa Felthåndbok</span>
              <span>Generert: ${new Date().toLocaleDateString('no-NO')}</span>
            </div>
          </div>
        `;
      }
      
      // Vent på at bilder laster
      const images = container.querySelectorAll('img');
      await Promise.all(Array.from(images).map(img => {
        return new Promise((resolve) => {
          if (img.complete) {
            resolve();
          } else {
            img.onload = resolve;
            img.onerror = resolve;
          }
        });
      }));
      
      // Bruk iframe for PDF-print (ødelegger ikke React DOM)
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: serif; background: white; }
            @page { margin: 20mm; size: A4; }
          </style>
        </head>
        <body>${container.innerHTML}</body>
        </html>
      `);
      iframeDoc.close();

      // Vent på at bilder i iframe laster
      const iframeImages = iframeDoc.querySelectorAll('img');
      await Promise.all(Array.from(iframeImages).map(img =>
        new Promise(resolve => {
          if (img.complete) resolve();
          else { img.onload = resolve; img.onerror = resolve; }
        })
      ));

      iframe.contentWindow.print();

      // Rydd opp
      setTimeout(() => {
        document.body.removeChild(iframe);
        document.body.removeChild(container);
      }, 1000);
      setShowDownloadMenu(false);
      
    } catch (error) {
      console.error('PDF nedlasting feilet:', error);
      alert('PDF nedlasting feilet. Prøv igjen.');
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-12 pb-40 animate-in zoom-in-95 duration-500">
      <div className="flex justify-between items-center px-2">
        <button 
          onClick={() => setStyle(s => s === 'clean' ? 'classic' : 'clean')} 
          className="bg-white px-6 py-3 rounded border border-[#D1CCC0] text-[10px] font-black uppercase tracking-widest text-[#3B4D3C] shadow-sm hover:bg-[#F8F5F0] transition-colors"
        >
          <Book size={16} className="inline mr-2" /> {style.toUpperCase()}
        </button>
        <div className="flex gap-4">
          <button 
            onClick={() => onEdit(item)} 
            className="p-4 bg-white rounded border border-[#D1CCC0] text-[#3B4D3C] shadow-sm hover:scale-105 active:scale-95 transition-all"
            aria-label="Rediger fangst"
            title="Rediger fangst"
          >
            <Pencil size={24}/>
          </button>
          <button 
            onClick={() => onZoom(item.image)} 
            className="p-4 bg-white rounded border border-[#D1CCC0] text-[#3B4D3C] shadow-sm hover:scale-105 active:scale-95 transition-all"
          >
            <Maximize2 size={24}/>
          </button>
          
          {/* Nedlastingsmeny */}
          <div className="relative" ref={downloadMenuRef}>
            <button 
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              className="p-4 bg-white rounded border border-[#D1CCC0] text-[#3B4D3C] shadow-sm hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
            >
              <DownloadCloud size={24}/>
              <ChevronDown size={16} className={`transition-transform ${showDownloadMenu ? 'rotate-180' : ''}`} />
            </button>
            
            {showDownloadMenu && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-[#D1CCC0] shadow-xl rounded-lg overflow-hidden z-50">
                <div className="p-3 border-b border-[#D1CCC0] bg-[#F8F5F0]">
                  <p className="font-sans text-xs font-semibold text-[#3B4D3C] uppercase tracking-wider">
                    Last ned fangstkort
                  </p>
                </div>
                
                {/* Bilde nedlastinger */}
                <div className="p-2">
                  <p className="font-sans text-xs text-[#3B4D3C]/60 uppercase tracking-wider px-2 py-1">
                    Som bilde (JPG)
                  </p>
                  <button
                    onClick={() => downloadAsImage('clean')}
                    className="w-full px-3 py-2 text-left font-sans text-sm hover:bg-[#F8F5F0] transition-colors flex items-center gap-2 text-[#3B4D3C] rounded"
                  >
                    <FileImage size={16} />
                    Clean stil
                  </button>
                  <button
                    onClick={() => downloadAsImage('classic')}
                    className="w-full px-3 py-2 text-left font-sans text-sm hover:bg-[#F8F5F0] transition-colors flex items-center gap-2 text-[#3B4D3C] rounded"
                  >
                    <FileImage size={16} />
                    Classic stil
                  </button>
                </div>
                
                {/* PDF nedlastinger */}
                <div className="p-2 border-t border-[#D1CCC0]/30">
                  <p className="font-sans text-xs text-[#3B4D3C]/60 uppercase tracking-wider px-2 py-1">
                    Som PDF
                  </p>
                  <button
                    onClick={() => downloadAsPDF('classic')}
                    className="w-full px-3 py-2 text-left font-sans text-sm hover:bg-[#F8F5F0] transition-colors flex items-center gap-2 text-[#3B4D3C] rounded"
                  >
                    <FileText size={16} />
                    Classic stil
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`relative w-full shadow-2xl bg-white ${
        style === 'clean' 
          ? 'rounded-[60px] overflow-hidden aspect-[4/5]' 
          : 'p-10 border border-[#D1CCC0] aspect-auto'
      }`}>
        {style === 'clean' ? (
          <>
            <img 
              src={item.image} 
              onClick={() => onZoom(item.image)}
              className="w-full h-full object-cover grayscale-[0.1] cursor-zoom-in" 
              alt={item.species} 
            />
            <div onClick={() => onZoom(item.image)} className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent p-12 flex flex-col justify-end cursor-zoom-in">
              <h2 className="text-6xl font-serif font-black italic uppercase leading-none text-white mb-6 tracking-tighter">
                {item.species}
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between items-end border-t border-white/20 pt-6">
                  {item.weight && (
                    <div>
                      <p className="text-[10px] font-black uppercase text-white/40 tracking-widest mb-1">Vekt</p>
                      <p className="text-4xl font-serif font-black italic text-[#E67E22]">{fmtWeight(item.weight)}</p>
                    </div>
                  )}
                  <div className="text-right">
                    <p className="text-[10px] font-black uppercase text-white/40 tracking-widest mb-1">Feltdato</p>
                    <p className="font-serif italic text-xl text-white">
                      {new Date(item.caughtAt).toLocaleDateString('no-NO')}
                    </p>
                    {item.time && (
                      <p className="font-serif italic text-sm text-white/60 mt-1">
                        {item.time.includes(':') ? item.time : `${item.time.slice(0,2)}:${item.time.slice(2)}`}
                      </p>
                    )}
                  </div>
                </div>
                
                {(item.weather?.temp || item.weather?.wind || item.weather?.condition) && (
                  <div className="flex gap-4 text-white/80 text-sm font-serif italic border-t border-white/10 pt-4">
                    {item.weather?.temp && <span>{item.weather.temp}°C</span>}
                    {item.weather?.wind && <span>{item.weather.wind} m/s</span>}
                    {item.weather?.condition && <span>{item.weather.condition}</span>}
                    {item.weather?.windDir && <span>Fra {item.weather.windDir}</span>}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-10 relative">
            <div className="flex justify-between items-start border-b-2 border-[#3B4D3C]/10 pb-8">
              <div className="max-w-[75%] space-y-2">
                <p className="text-[11px] font-black uppercase text-[#E67E22] tracking-[0.5em] italic">
                  Fangstrapport
                </p>
                <h2 className="text-6xl font-serif font-black italic uppercase leading-none text-[#2D3436] tracking-tighter">
                  {item.species}
                </h2>
                <p className="font-serif italic text-xl text-[#3B4D3C]/50">{item.latin}</p>
              </div>
            </div>

            <img 
              src={item.image} 
              onClick={() => onZoom(item.image)}
              className="w-full aspect-[16/9] object-cover rounded shadow-xl border border-[#D1CCC0]/20 grayscale-[0.2] cursor-zoom-in" 
              alt="Specimen" 
            />

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 border-t border-[#3B4D3C]/10 pt-10">
              <HeritageStat label="Dato" value={new Date(item.caughtAt).toLocaleDateString('no-NO')} />
              {item.time && <HeritageStat label="Klokkeslett" value={item.time.includes(':') ? item.time : `${item.time.slice(0,2)}:${item.time.slice(2)}`} />}
              {item.weight && <HeritageStat label="Målt Vekt" value={fmtWeight(item.weight)} />}
              {item.weather?.temp && <HeritageStat label="Temperatur" value={`${item.weather.temp}°C`} />}
              {item.weather?.wind && <HeritageStat label="Vind" value={`${item.weather.wind} m/s`} />}
              {item.weather?.condition && <HeritageStat label="Forhold" value={item.weather.condition} />}
              {item.weather?.windDir && <HeritageStat label="Vindretning" value={item.weather.windDir} />}
              <HeritageStat label="Habitat" value={item.waterType === 'salt' ? 'Saltvann' : 'Ferskvann'} />
            </div>

            {(item.advanced?.length || item.advanced?.circumference || item.advanced?.width || item.advanced?.gear || item.advanced?.lure || item.advanced?.notes) && (
              <div className="bg-[#F8F5F0] p-8 rounded border border-[#D1CCC0]/30 space-y-6">
                <h3 className="text-[11px] font-black uppercase text-[#3B4D3C]/60 tracking-[0.3em] italic border-b border-[#D1CCC0]/40 pb-3">
                  Mål & Utstyr
                </h3>
                
                {(item.advanced?.length || item.advanced?.circumference || item.advanced?.width) && (
                  <div className="grid grid-cols-3 gap-6">
                    {item.advanced?.length && <HeritageStat label="Lengde" value={`${item.advanced.length} cm`} />}
                    {item.advanced?.circumference && <HeritageStat label="Omkrets" value={`${item.advanced.circumference} cm`} />}
                    {item.advanced?.width && <HeritageStat label="Bredde" value={`${item.advanced.width} cm`} />}
                  </div>
                )}
                
                {(item.advanced?.gear || item.advanced?.lure) && (
                  <div className="grid grid-cols-2 gap-6 pt-4 border-t border-[#D1CCC0]/40">
                    {item.advanced?.gear && <HeritageStat label="Utstyr" value={item.advanced.gear} />}
                    {item.advanced?.lure && <HeritageStat label="Agn/Sluk" value={item.advanced.lure} />}
                  </div>
                )}
                
                {item.advanced?.notes && (
                  <div className="space-y-2 pt-4 border-t border-[#D1CCC0]/40">
                    <label className="text-[9px] font-black uppercase text-[#3B4D3C]/40 italic tracking-widest">
                      Notater
                    </label>
                    <p className="font-serif italic text-lg leading-relaxed text-[#3B4D3C] whitespace-pre-line border-l-2 border-[#E67E22]/20 pl-4">
                      {item.advanced.notes}
                    </p>
                  </div>
                )}
              </div>
            )}

            {item.location && item.privacy && item.privacy !== 'Ingen' && (
              <div className="bg-[#F8F5F0] p-8 rounded border border-[#D1CCC0]/30 space-y-4">
                <h3 className="text-[11px] font-black uppercase text-[#3B4D3C]/60 tracking-[0.3em] italic border-b border-[#D1CCC0]/40 pb-3">
                  Posisjon
                </h3>
                
                {/* Kart */}
                <div className="h-64 rounded-md overflow-hidden border border-[#D1CCC0] shadow-inner">
                  <MapContainer
                    center={[item.location.lat, item.location.lng]}
                    zoom={item.privacy === 'Nøyaktig' ? 15 : item.privacy === '100m' ? 14 : 12}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                    dragging={false}
                    zoomControl={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    {item.privacy === 'Nøyaktig' ? (
                      <Marker position={[item.location.lat, item.location.lng]} icon={orangeIcon} />
                    ) : (
                      <Circle
                        center={[item.location.lat, item.location.lng]}
                        radius={item.privacy === '100m' ? 100 : 1000}
                        pathOptions={{
                          color: '#E67E22',
                          fillColor: '#E67E22',
                          fillOpacity: 0.2,
                          weight: 2
                        }}
                      />
                    )}
                  </MapContainer>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="space-y-2">
                    <p className="text-[10px] font-black uppercase text-[#3B4D3C]/50 tracking-[0.3em] italic">
                      Nøyaktighet
                    </p>
                    <p className="text-xl font-serif font-black italic text-[#2D3436]">
                      {item.privacy}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[10px] font-black uppercase text-[#3B4D3C]/50 tracking-[0.3em] italic">
                      Koordinater
                    </p>
                    <p className="text-sm font-mono text-[#3B4D3C]/60">
                      {item.privacy === 'Nøyaktig' 
                        ? `${item.location.lat.toFixed(6)}, ${item.location.lng.toFixed(6)}`
                        : `±${item.privacy}`
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-8 flex justify-between items-center opacity-30 italic font-serif text-[10px] border-t border-dashed border-[#D1CCC0]">
              <span>Signert: FiskDa Felthåndbok</span>
              <span>Ref: NO-{item.caughtAt.toString().slice(-6)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 sm:gap-8 pt-6">
        <button 
          onClick={() => onShare(item)} 
          className="bg-[#3B4D3C] text-[#F8F5F0] py-4 sm:py-6 rounded shadow-xl font-serif font-black italic flex items-center justify-center gap-2 sm:gap-4 hover:bg-[#2D3A2D] hover:scale-[1.02] active:scale-95 transition-all text-base sm:text-2xl tracking-wider"
        >
          {copyFeedback ? <Check size={20} className="sm:w-7 sm:h-7" /> : <Share2 size={20} className="sm:w-7 sm:h-7" />} 
          <span className="hidden sm:inline">{copyFeedback === 'copied' ? 'LINK KOPIERT' : copyFeedback === 'shared' ? 'DELT' : 'DEL FANGSTKORT'}</span>
          <span className="sm:hidden">{copyFeedback === 'copied' ? 'KOPIERT' : copyFeedback === 'shared' ? 'DELT' : 'DEL'}</span>
        </button>
        <button 
          onClick={() => onDelete(item.id)} 
          className="bg-white text-red-600 border border-red-100 py-4 sm:py-6 rounded shadow-lg font-serif font-black italic flex items-center justify-center gap-2 sm:gap-4 hover:bg-red-50 active:scale-95 transition-all text-base sm:text-xl"
        >
          <Trash2 size={20} className="sm:w-6 sm:h-6" /> 
          <span className="hidden sm:inline">SLETT SIDE</span>
          <span className="sm:hidden">SLETT</span>
        </button>
      </div>
    </div>
  );
}

function HeritageStat({ label, value }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-black uppercase text-[#3B4D3C]/50 tracking-[0.3em] italic">
        {label}
      </p>
      <p className="text-xl font-serif font-black italic text-[#2D3436] truncate leading-none">
        {value}
      </p>
    </div>
  );
}